import { listConsultations } from "@/core/clinical/clinical-store";
import {
  countLabAwaitingResult,
  countPendingLabOrders,
} from "@/core/laboratory/lab-store";
import {
  checkImagingExamInsurerGate,
  ensureCoverageCaseForImagingExam,
  linkCoverageCaseToImagingExam,
} from "@/core/insurance/coverage-case-flow";
import { enqueueTicket } from "@/core/queue/service-queue-store";
import {
  getAppointmentPractitionerById,
  practitionerDisplayName,
} from "@/core/practitioners/practitioner-store";
import { listPatients } from "@/core/patients/patient-store";
import { autoBillImagingExamOnComplete } from "@/core/billing/act-completion-billing";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import { apiListImagingExams, apiSyncImagingExam } from "@/core/hospitol-api-client";
import type {
  ImagingExam,
  ImagingExamInput,
  ImagingExamStatus,
  ImagingReportSaveInput,
  InsurerApprovalStatus,
} from "./types";
import {
  buildImagingReportText,
  normalizeImagingStructuredReport,
} from "./types";

export type { ImagingExam, ImagingExamInput, ImagingExamStatus } from "./types";
export {
  COMMON_IMAGING_TYPES,
  formatImagingReportSummary,
  IMAGING_STATUS_LABELS,
  imagingExamConsultationLabel,
} from "./types";

export interface ImagingExamCreateResult {
  exam: ImagingExam;
  insurerPending: boolean;
  coverageCaseId: string | null;
  alertMessage: string | null;
}

const EXAMS_KEY = "hospitol.imagingExams";
const SEQ_KEY = "hospitol.imagingExamSeq";
const DEMO_SEED_KEY = "hospitol.imaging.demoSeeded";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  setStorageItem(key, JSON.stringify(value));
}

function nextExamNumber(): string {
  const seq = Number(getStorageItem(SEQ_KEY) ?? "0") + 1;
  setStorageItem(SEQ_KEY, String(seq));
  return `IMG-${String(seq).padStart(5, "0")}`;
}

function sortByScheduledDesc(a: ImagingExam, b: ImagingExam): number {
  return new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime();
}

function normalizeImagingExam(raw: Record<string, unknown>): ImagingExam {
  const consultationId = raw.consultationId ? String(raw.consultationId) : null;
  const legacyText = String(raw.reportText ?? "");
  const structured = normalizeImagingStructuredReport(
    {
      technique: raw.reportTechnique,
      findings: raw.reportFindings,
      conclusion: raw.reportConclusion,
    },
    legacyText,
  );
  const reportText =
    legacyText.trim() || (structured.findings || structured.conclusion ? buildImagingReportText(structured) : "");

  return {
    id: String(raw.id ?? ""),
    examNumber: String(raw.examNumber ?? ""),
    patientId: String(raw.patientId ?? ""),
    consultationId,
    prescriberId: String(raw.prescriberId ?? ""),
    prescriber: String(raw.prescriber ?? ""),
    examType: String(raw.examType ?? ""),
    bodyPart: String(raw.bodyPart ?? ""),
    clinicalIndication: String(raw.clinicalIndication ?? ""),
    scheduledAt: String(raw.scheduledAt ?? new Date().toISOString()),
    status: (raw.status as ImagingExamStatus) ?? "scheduled",
    insurerApproval: (raw.insurerApproval as InsurerApprovalStatus) ?? "none",
    coverageCaseId: raw.coverageCaseId ? String(raw.coverageCaseId) : null,
    reportTechnique: structured.technique,
    reportFindings: structured.findings,
    reportConclusion: structured.conclusion,
    reportText,
    reportFileName: String(raw.reportFileName ?? ""),
    completedAt: raw.completedAt ? String(raw.completedAt) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readImagingExams(): ImagingExam[] {
  return readJson<Record<string, unknown>[]>(EXAMS_KEY, []).map(normalizeImagingExam);
}

function replaceImagingExamsCache(exams: ImagingExam[]): void {
  writeJson(EXAMS_KEY, exams);
}

function replicateImagingExamToApi(exam: ImagingExam): void {
  void apiSyncImagingExam(exam);
}

export class ImagingExamValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImagingExamValidationError";
  }
}

export function ensureDemoImagingExams() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const patient = listPatients()[1] ?? listPatients()[0];
  if (!patient) return;

  const consultation = listConsultations().find((c) => c.patientId === patient.id);

  createImagingExam({
    patientId: patient.id,
    prescriberId: "demo-prac-mukendi",
    consultationId: consultation?.id ?? null,
    examType: "Radiographie",
    bodyPart: "Thorax",
    clinicalIndication: "Toux persistante",
    scheduledAt: new Date(Date.now() + 86_400_000).toISOString(),
  });

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listImagingExams(): ImagingExam[] {
  ensureDemoImagingExams();
  return readImagingExams().sort(sortByScheduledDesc);
}

/** Télécharge les examens imagerie depuis l'API et met à jour le cache local. */
export async function syncImagingExamsFromApi(): Promise<boolean> {
  const remote = await apiListImagingExams();
  if (!remote) return false;
  if (remote.length > 0) {
    replaceImagingExamsCache(remote);
    return true;
  }
  const local = readImagingExams();
  if (local.length > 0) {
    for (const exam of local) {
      replicateImagingExamToApi(exam);
    }
    return true;
  }
  return true;
}

export function listImagingExamsByStatus(status: ImagingExamStatus): ImagingExam[] {
  return listImagingExams().filter((e) => e.status === status);
}

export function listImagingExamsByPatient(patientId: string): ImagingExam[] {
  return listImagingExams().filter((e) => e.patientId === patientId);
}

export function listImagingExamsByConsultation(consultationId: string): ImagingExam[] {
  return listImagingExams().filter((e) => e.consultationId === consultationId);
}

export function getImagingExamById(id: string): ImagingExam | null {
  return listImagingExams().find((e) => e.id === id) ?? null;
}

export function createImagingExam(input: ImagingExamInput): ImagingExamCreateResult {
  if (!input.prescriberId.trim()) {
    throw new ImagingExamValidationError("Prescripteur requis.");
  }
  if (!input.examType.trim()) {
    throw new ImagingExamValidationError("Type d'examen requis.");
  }

  const gate = checkImagingExamInsurerGate({
    patientId: input.patientId,
    examType: input.examType,
    bodyPart: input.bodyPart,
    consultationId: input.consultationId,
  });

  let insurerApproval: InsurerApprovalStatus = "none";
  let status: ImagingExamStatus = "scheduled";
  let coverageCaseId: string | null = null;
  let alertMessage: string | null = null;

  if (gate.validationRequired && !gate.allowed) {
    insurerApproval = "pending";
    status = "pending_insurer";
    const label = [input.examType, input.bodyPart].filter(Boolean).join(" — ");
    alertMessage =
      `Demande enregistrée — en attente de validation assureur pour « ${label} ». ` +
      "L'imagerie peut planifier l'examen. Le patient peut aussi payer une proforma en attendant.";
  } else if (gate.validationRequired && gate.allowed) {
    insurerApproval = "approved";
  }

  const practitioner = getAppointmentPractitionerById(input.prescriberId);
  const prescriber =
    input.prescriber?.trim() ||
    practitioner?.displayName ||
    practitionerDisplayName(input.prescriberId, input.prescriberId);

  const now = new Date().toISOString();
  const exam: ImagingExam = {
    id: `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    examNumber: nextExamNumber(),
    patientId: input.patientId,
    consultationId: input.consultationId ?? null,
    prescriberId: input.prescriberId.trim(),
    prescriber,
    examType: input.examType.trim(),
    bodyPart: input.bodyPart?.trim() ?? "",
    clinicalIndication: input.clinicalIndication?.trim() ?? "",
    scheduledAt: input.scheduledAt ?? now,
    status,
    insurerApproval,
    coverageCaseId: null,
    reportTechnique: "",
    reportFindings: "",
    reportConclusion: "",
    reportText: "",
    reportFileName: "",
    completedAt: null,
    createdAt: now,
    updatedAt: now,
  };
  const list = readImagingExams();
  list.unshift(exam);
  writeJson(EXAMS_KEY, list);

  if (insurerApproval === "pending") {
    const label = [exam.examType, exam.bodyPart].filter(Boolean).join(" — ");
    const coverageCase = ensureCoverageCaseForImagingExam({
      patientId: exam.patientId,
      examLabel: label,
      consultationId: exam.consultationId,
      imagingExamId: exam.id,
    });
    if (coverageCase) {
      coverageCaseId = coverageCase.id;
      linkCoverageCaseToImagingExam(coverageCase.id, exam.id);
      exam.coverageCaseId = coverageCaseId;
      list[0] = exam;
      writeJson(EXAMS_KEY, list);
    }
  }

  enqueueTicket({
    patientId: exam.patientId,
    serviceId: "imaging",
    unitName: "Imagerie",
    motif: exam.examType,
  });

  replicateImagingExamToApi(exam);

  return { exam, insurerPending: insurerApproval === "pending", coverageCaseId, alertMessage };
}

export function updateImagingExamStatus(
  id: string,
  status: ImagingExamStatus,
): ImagingExam | null {
  const list = readImagingExams();
  const index = list.findIndex((e) => e.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const updated: ImagingExam = {
    ...list[index]!,
    status,
    updatedAt: now,
    completedAt: status === "completed" ? now : list[index]!.completedAt,
  };
  list[index] = updated;
  writeJson(EXAMS_KEY, list);
  replicateImagingExamToApi(updated);
  return updated;
}

export function patchImagingExamInsurerApproval(
  id: string,
  insurerApproval: InsurerApprovalStatus,
): ImagingExam | null {
  const list = readImagingExams();
  const index = list.findIndex((e) => e.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  let status = list[index]!.status;
  if (status === "pending_insurer" && insurerApproval === "approved") {
    status = "scheduled";
  }

  const updated: ImagingExam = {
    ...list[index]!,
    insurerApproval,
    status,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(EXAMS_KEY, list);
  replicateImagingExamToApi(updated);
  return updated;
}

export function saveImagingReport(
  id: string,
  report: ImagingReportSaveInput | string,
  reportFileName?: string,
): ImagingExam | null {
  const list = readImagingExams();
  const index = list.findIndex((e) => e.id === id);
  if (index < 0) return null;

  const structured =
    typeof report === "string"
      ? normalizeImagingStructuredReport(null, report)
      : normalizeImagingStructuredReport(report);

  const fileName =
    typeof report === "string"
      ? (reportFileName?.trim() ?? list[index]!.reportFileName)
      : (report.reportFileName?.trim() ?? reportFileName?.trim() ?? list[index]!.reportFileName);

  const reportText = buildImagingReportText(structured) || (typeof report === "string" ? report.trim() : "");

  const now = new Date().toISOString();
  const updated: ImagingExam = {
    ...list[index]!,
    reportTechnique: structured.technique,
    reportFindings: structured.findings,
    reportConclusion: structured.conclusion,
    reportText,
    reportFileName: fileName,
    status: "completed",
    completedAt: now,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(EXAMS_KEY, list);
  replicateImagingExamToApi(updated);
  void autoBillImagingExamOnComplete(updated);
  return updated;
}

export function countPendingImagingExams(): number {
  return listImagingExams().filter(
    (e) => e.status === "scheduled" || e.status === "in_progress",
  ).length;
}

export function getPendingDiagnosticsStats(): {
  labPending: number;
  labAwaitingResult: number;
  imagingPending: number;
  total: number;
} {
  const labPending = countPendingLabOrders();
  const labAwaitingResult = countLabAwaitingResult();
  const imagingPending = countPendingImagingExams();
  return {
    labPending,
    labAwaitingResult,
    imagingPending,
    total: labPending + imagingPending,
  };
}
